import TableContainer from "components/Common/TableContainer"
import React, { useEffect, useMemo, useState, useCallback } from "react"
import { useDebounce } from "hooks/useDebounce"
import { withRouter } from "react-router-dom"
import { Card, CardBody, Col, Container, Row } from "reactstrap"
import * as url from "../../helpers/url_helper"

import { Name, Email, UserGroups, State } from "./userCol"

//Import Breadcrumb
import Breadcrumbs from "components/Common/Breadcrumb"

import {
    getUsers as onGetUsers,
    clearUsers,
    manageUser,
    clearManageError,
} from "store/users/actions"

//redux
import AccessControlWrapper from "components/Common/PermissionButton"
import { useDispatch, useSelector } from "react-redux"
import { PageTitle } from "constants/ui"
import IconButton from "components/Common/IconButton"
import CButton from "components/Common/CButton"
import ConfirmButton from "components/Common/ConfirmButton"
import { getFirstTabPermission } from "helpers/tab_helper"
import { calculateStartIndex } from "helpers/pagination_helper"
import UserModal from "./user-modal"

const UserList = props => {
    //meta title
    document.title = PageTitle.USER_LIST

    const dispatch = useDispatch()
    const { history } = props

    const [search, setSearch] = useState("")
    const [page, setPage] = useState(1)
    const [isSubmitting, setIsSubmitting] = useState(false)

    // Modal state
    const [modalState, setModalState] = useState({
        isOpen: false,
        mode: null, // "add" or "edit"
        userData: null,
    })

    const { data, state, userMenu } = useSelector(state => ({
        data: state.users,
        state: state,
        userMenu: state.userMenu?.userMenu || [],
    }))

    const firstTabPermission = getFirstTabPermission("USER_DETAIL", userMenu)

    const handleActivateUser = useCallback(userID => {
        setIsSubmitting(true)
        dispatch(manageUser(
            { actionType: "activate", userID: userID.toString() },
            {
                headers: { "input-parameters": {} },
                refreshPagination: { startIndex: calculateStartIndex(page), keyword: search || "" }
            },
            history
        ))
    }, [dispatch, history, page, search])

    const handleDeactivateUser = useCallback(userID => {
        setIsSubmitting(true)
        dispatch(manageUser(
            { actionType: "deactivate", userID: userID.toString() },
            {
                headers: { "input-parameters": {} },
                refreshPagination: { startIndex: calculateStartIndex(page), keyword: search || "" }
            },
            history
        ))
    }, [dispatch, history, page, search])

    // Modal handlers - defined before columns to avoid reference error
    const handleOpenAddModal = useCallback(() => {
        setModalState({
            isOpen: true,
            mode: "add",
            userData: null,
        })
    }, [])

    const handleOpenViewModal = useCallback(user => {
        setModalState({
            isOpen: true,
            mode: "edit",
            userData: user,
        })
    }, [])

    const handleCloseModal = useCallback(() => {
        setModalState({
            isOpen: false,
            mode: null,
            userData: null,
        })
    }, [])

    const handleModalSuccess = useCallback(() => {
        // The saga already handles list refresh with correct pagination via refreshPagination
        // Just close the modal - no need to dispatch getUsers again
        handleCloseModal()
    }, [handleCloseModal])

    const columns = useMemo(
        () => [
            {
                Header: "First Name",
                accessor: "firstName",
                filterable: true,
                disableFilters: true,
                Cell: cellProps => {
                    return <Name {...cellProps} />
                },
            },
            {
                Header: "Last Name",
                accessor: "lastName",
                filterable: true,
                disableFilters: true,
                Cell: cellProps => {
                    return <Name {...cellProps} />
                },
            },
            {
                Header: "Email",
                accessor: "email",
                filterable: true,
                disableFilters: true,
                Cell: cellProps => {
                    return <Email {...cellProps} />
                },
            },
            {
                Header: "Mobile",
                accessor: "mobile",
                filterable: true,
                disableFilters: true,
                Cell: cellProps => {
                    return <Name {...cellProps} />
                },
            },
            {
                Header: "User Groups",
                accessor: "userGroups",
                filterable: true,
                disableFilters: true,
                Cell: cellProps => {
                    return <UserGroups {...cellProps} />
                },
            },
            {
                Header: "State",
                accessor: "userState",
                filterable: true,
                disableFilters: true,
                Cell: cellProps => {
                    return <State {...cellProps} />
                },
            },
            {
                Header: "Action",
                Cell: cellProps => {
                    const user = cellProps.row.original
                    const isActive = user.userState === "ACTIVE"

                    return (
                        <div className="d-flex gap-2">
                            <AccessControlWrapper
                                actionType={firstTabPermission?.actionType || "get"}
                                contextUri={firstTabPermission?.contextUri || url.GET_USERS}
                                history={history}
                            >
                                <IconButton.View
                                    onClick={() => {
                                        const user = cellProps.row.original
                                        handleOpenViewModal(user)
                                    }}
                                />
                            </AccessControlWrapper>

                            {/* Activate/Deactivate Button */}
                            {isActive ? (
                                <AccessControlWrapper
                                    actionType="post"
                                    contextUri={url.MANAGE_USER}
                                    history={history}
                                >
                                    <ConfirmButton
                                        contextUri={url.MANAGE_USER}
                                        actionType="post"
                                        onConfirm={() => handleDeactivateUser(user.userID)}
                                        message={`Are you sure you want to deactivate this user?`}
                                        disabled={isSubmitting}
                                        renderButton={
                                            <IconButton
                                                icon="bx bx-no-entry"
                                                tooltip="Deactivate"
                                                color="warning"
                                                title="Deactivate"
                                            />
                                        }
                                    />
                                </AccessControlWrapper>
                            ) : (
                                <AccessControlWrapper
                                    actionType="post"
                                    contextUri={url.MANAGE_USER}
                                    history={history}
                                >
                                    <ConfirmButton
                                        contextUri={url.MANAGE_USER}
                                        actionType="post"
                                        onConfirm={() => handleActivateUser(user.userID)}
                                        message={`Are you sure you want to activate this user?`}
                                        disabled={isSubmitting}
                                        renderButton={
                                            <IconButton
                                                icon="bx bx-play"
                                                color="success"
                                                title="Activate"
                                            />
                                        }
                                    />
                                </AccessControlWrapper>
                            )}
                        </div>
                    )
                },
            },
        ],
        [handleActivateUser, handleDeactivateUser, isSubmitting, history, handleOpenViewModal]
    )

    // Load fresh users data when component mounts
    useEffect(() => {
        dispatch(
            onGetUsers({
                headers: {
                    "input-parameters": {},
                },
                pagination: { startIndex: 0 },
            })
        )
    }, [dispatch])

    // Clear users and manage errors when component mounts/unmounts
    useEffect(() => {
        // Clear any lingering manage errors when component mounts
        dispatch(clearManageError())

        return () => {
            dispatch(clearUsers())
            dispatch(clearManageError())
        }
    }, [dispatch])

    // Handle manage user operations completion (success or failure)
    useEffect(() => {
        if (data?.loading === false && isSubmitting) {
            setIsSubmitting(false)
        }
    }, [data?.loading, isSubmitting])

    const rowClick = (e, row) => {
        const { original: user } = row
        handleOpenViewModal(user)
    }

    const dispatchSearch = useCallback((value) => {
        setPage(1)
        dispatch(
            onGetUsers({
                headers: {
                    "input-parameters": { keyword: value || "" },
                },
                pagination: { startIndex: 0 },
            })
        )
    }, [dispatch])

    const [debouncedSearch] = useDebounce(dispatchSearch)

    const handleSearchChange = e => {
        const { value } = e.target
        setSearch(value)
        debouncedSearch(value)
    }

    const handlePageChange = page => {
        const startIndex = (page - 1) * 10 // Assuming page size is 10
        setPage(page)
        dispatch(
            onGetUsers({
                headers: {
                    "input-parameters": { keyword: search || "" },
                },
                pagination: { startIndex },
            })
        )
    }

    const handleRefresh = () => {
        dispatch(
            onGetUsers({
                headers: {
                    "input-parameters": { keyword: search || "" },
                },
                pagination: { startIndex: (page - 1) * 10 },
            })
        )
    }

    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>
                    {/* Render Breadcrumbs */}
                    <Breadcrumbs
                        title={PageTitle.USER_LIST}
                        breadcrumbItem={PageTitle.USER_LIST}
                    />

                    <Row>
                        <Col lg="12">
                            <Card>
                                <CardBody>
                                    <Row>
                                        <Col md="6">
                                            <div>
                                                <input
                                                    type="text"
                                                    placeholder="search users..."
                                                    value={search}
                                                    onChange={handleSearchChange}
                                                    className="form-control w-50 rounded-pill"
                                                />
                                            </div>
                                        </Col>
                                        <Col md="6">
                                            <div className="text-sm-end">
                                                <AccessControlWrapper
                                                    actionType={"post"}
                                                    contextUri={url.MANAGE_USER}
                                                    history={history}
                                                >
                                                    <CButton.Primary
                                                        icon="mdi mdi-plus-circle-outline"
                                                        text="Add User"
                                                        className="mb-2 me-2"
                                                        onClick={handleOpenAddModal}
                                                    />
                                                </AccessControlWrapper>
                                            </div>
                                        </Col>
                                    </Row>

                                    {/* Manage User Error Alert */}
                                    {data?.manageError?.message && (
                                        <Row className="mb-3">
                                            <Col lg="12">
                                                <div className="alert alert-danger" role="alert">
                                                    <strong>User Action Error:</strong> {data.manageError.message}
                                                </div>
                                            </Col>
                                        </Row>
                                    )}



                                    <TableContainer
                                        columns={columns}
                                        data={data?.error?.message ? [] : data.users || []}
                                        isGlobalFilter={false}
                                        isAddUserList={true}
                                        rowClick={rowClick}
                                        protectedRowClick={true}
                                        contextUri={url.GET_USERS}
                                        actionType="get"
                                        handleAddButtonClick={handleOpenAddModal}
                                        customPageSize={10}
                                        className="custom-header-css"
                                        columnFilter={false}
                                        changePageSize={false}
                                        isAddButtonText="Add User"
                                        onPageChange={handlePageChange}
                                        currentPage={page}
                                        hasNextPage={!data?.error?.message && data.users?.length >= 10}
                                        onRefresh={handleRefresh}
                                        refreshContextUri={url.GET_USERS}
                                        refreshActionType="get"
                                        history={history}
                                    />
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>

                    {/* User Modal */}
                    <UserModal
                        isOpen={modalState.isOpen}
                        toggle={handleCloseModal}
                        mode={modalState.mode}
                        userData={modalState.userData}
                        onSuccess={handleModalSuccess}
                        history={history}
                        currentPage={page}
                        searchKeyword={search}
                    />
                </Container>
            </div>
        </React.Fragment>
    )
}

export default withRouter(UserList)
